<template>
   <div class="d-flex flex-column" v-if="isReady">
        
        <div class="d-flex flex-row justify-content-between">
            <h2 class="p-3 d-flex align-items-center">{{isEdit ? 'Edit Rule' : 'New Rule'}}</h2>
            
            <div class="d-flex justify-content-end align-items-center my-4 px-4">
                <button 
                    type="button" 
                    class="btn cancel wider mr-3" 
                    @click="btnCancel">Cancel
                </button>
                <input
					type="submit"
					value="Save"
					class="btn wider mr-3"
                    :disabled="$v.ruleName.$invalid || $v.tableData.$invalid"
					@click="btnSave"/>
            </div>
        </div>

        <div class="new-product">
            <div class="my-4">
                <div class="px-4">
                    <div class="row">
                        <div class="col-sm-12">
                            <div class="panel mt-0 shaded radius-top-left-none radius-top-right-none">
                                <h5>Rule Details:</h5>
                                <div class="row mt-3 row-min-height">
                                    <div class="col-xl-5 col-lg-4 col-md-6 mb-3 mr-5">
                                        <label class="full">Name<span class="brand ml-1">*</span>
                                            <input
                                                type="text"
                                                class="bg-lighter btn-height"
                                                v-model="ruleName"/>
                                        </label>

                                        <div v-if="$v.ruleName.$invalid" class="messages error">
                                            <span v-if="!$v.ruleName.required">Name is required.</span>
                                            <span v-if="!$v.ruleName.minLength">Name must have at least 5 characters.</span>
                                             <span v-if="!$v.ruleName.validCharacters">
                                                Invalid characters. Please use only letters, numbers, spaces, and the following special characters: <strong>{{specialCharacters}}</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <h5>Rules Definition:</h5>

                                <div class="row mb-3 mt-4 var-min-height">
                                    <div class="d-flex justify-content-center col-12 pl-5">
                                        <div class="d-flex flex-column col-3 p-0 mr-5 var-min-height">
                                            <label class="full">Variable - Row<span class="brand pl-1">*</span></label>

                                            <div>
                                                <v-select
                                                    v-model="tableData.variables.row"
                                                    :disabled="isEdit"
                                                    autocomplete="no"
                                                    :options="rowOptions"
                                                    @input="updateColumn">
                                                </v-select>
                                            </div>

                                            <div v-if="$v.tableData.variables.row.$invalid" class="mt-1 messages error">
                                                <span v-if="!$v.tableData.variables.row.required">Row is required.</span>
                                            </div>
                                        </div>

                                        <div class="d-flex flex-column col-3 p-0 mr-5 var-min-height">
                                            <label class="full">Variable - Column<span class="brand pl-1">*</span></label>

                                            <div>
                                                <v-select
                                                    v-model="tableData.variables.column"
                                                    :disabled="!tableData.variables.row || isEdit"
                                                    autocomplete="no"
                                                    :options="columnOptions"
                                                    @change="updateAction">
                                                </v-select>
                                            </div>

                                            <div v-if="$v.tableData.variables.column.$invalid" class="mt-1 messages error">
                                                <span v-if="!$v.tableData.variables.column.required">Column is required.</span>
                                            </div>
                                        </div>

                                        <div class="d-flex flex-column col-3 p-0 mr-5 var-min-height">
                                            <label class="full">Action - Inside<span class="brand pl-1">*</span></label>

                                            <div>
                                                <v-select
                                                    v-model="tableData.variables.action"
                                                    :disabled="(!tableData.variables.row || !tableData.variables.column) || isEdit"
                                                    autocomplete="no"
                                                    :options="actionOptions"
                                                    @change="initTable">
                                                </v-select>
                                            </div>

                                            <div v-if="$v.tableData.variables.action.$invalid" class="mt-1 messages error">
                                                <span v-if="!$v.tableData.variables.action.required">Action is required.</span>
                                            </div>

                                            <div v-if="validateIncreaseMsg" class="mt-2 fs-small dark bold">
                                                <span>This action will increase the base price without affecting the settings for the sales channel.</span>
                                            </div>

                                            <div v-if="validateOverrideMsg" class="mt-2 fs-small dark bold">
                                                <span>This action will override the channel price increase configured in the product.</span>
                                            </div>
                                        </div>                                 
                                    </div>
                                </div>

                                <h5>Table:</h5>

                                <div class="table-min-height">
                                    <div class="d-flex justify-content-center">
                                        <table class="w-80 table-sales narrow" v-if="isTableReady">
                                            <!-- Channels Multiplier || Price Multiplier -->
                                            <thead v-if="isChannelsMultiplier || isPriceMultiplier">
        
                                                <tr class="thead-size">
                                                    <th width="25%" class="border-0"></th>
                                                    <th width="auto" 
                                                        class="text-center border-0" 
                                                        :colspan="`${tableData.tables[tableData.tableType][tableData.tableSubType].columns.filter(item => !item.isDeleted).length}`">
                                                        <span class="font-weight-700">{{tableColumn}}</span>
                                                    </th>
                                                </tr>
                                                
                                                <tr>
                                                    <th width="30%" class="text-left border-top-0"><span class="font-weight-700">{{tableLine}}</span></th>
                                                    <th v-for="col, index in tableData.tables[tableData.tableType][tableData.tableSubType].columns"
                                                        v-if="tableData.tableSubType !== 'weekDaysPerInput' && col.isDeleted === false"
                                                        :style="{ width: 70/tableData.tables[tableData.tableType][tableData.tableSubType].columns.filter(item => !item.isDeleted).length + '%' }" 
                                                        class="text-center border border-top-0">
                                                        
                                                        <div class="d-flex align-items-center justify-content-center">
                                                            <div class="d-flex align-items-center">
                                                                <input
                                                                    ref="inputField"
                                                                    @focus="selectText(index)"
                                                                    type="text"
                                                                    class="font-weight-700 fs-large input-col pr-0 text-right"
                                                                    v-model.number="col.val"
                                                                    @input="colLimitInput(tableData.tableType, index)"/>
                                                                    <div class="pl-1">%</div>
                                                            </div>
                                                            <i  class="ml-3 pr-0 fs-normal pointer fa fa fa-trash darker"
                                                                v-if="tableData.tables[tableData.tableType][tableData.tableSubType].columns.filter(item => !item.isDeleted).length !== 1"
                                                                @click="removeCol(tableData.tableType, tableData.tableSubType, index)"></i>
                                                        </div>
                                                    </th>

                                                    <th v-for="col, index in tableData.tables[tableData.tableType][tableData.tableSubType].columns"
                                                        v-if="tableData.tableSubType === 'weekDaysPerInput' && col.isDeleted === false"
                                                        :style="{ width: 65/tableData.tables[tableData.tableType][tableData.tableSubType].columns.filter(item => !item.isDeleted).length + '%' }" 
                                                        class="text-center border border-top-0">
                                                            
                                                        <div class="d-flex align-items-center justify-content-center">
                                                            <div class="d-flex align-items-center p-1">
                                                                <v-select
                                                                    v-model="col.day.val"
                                                                    autocomplete="no"
                                                                    label="val"
                                                                    :options="availableAbbreviationDays"
                                                                    class="select-width darkest"
                                                                    :style="{ width: '80px' }"
                                                                    @input="updateWeekDay(col, $event)">
                                                                </v-select>
                                                            </div>
                                                            <i  class="ml-3 pr-0 fs-normal pointer fa fa fa-trash darker"
                                                                v-if="tableData.tables[tableData.tableType][tableData.tableSubType].columns.filter(item => !item.isDeleted).length !== 1"
                                                                @click="removeCol(tableData.tableType, tableData.tableSubType, index)"></i>
                                                        </div>
                                                    </th>

                                                    <th width="5%" class="text-center border border-top-0 border-right-0">
                                                        <button class="btn-add d-flex justify-content-center align-items-center" 
                                                                @click="addColumn(tableData.tableType, tableData.tableSubType)" 
                                                                v-if="addColValidation">
                                                            
                                                            <i class="fs-normal fa fa-plus-circle dark"></i>
                                                        </button>
                                                    </th>
                                                </tr>
                                            </thead>
                                            
                                            <!-- Table Price Multiplier || Sales Number -->
                                            <thead v-if="isSeatsNumber">
                                                <tr class="thead-size">             
                                                    <th width="30%" class="border-top-0 border-left-0 border-right">{{tableLine}}</th>
                                                    <th width="35%" class="border-top-0 border-right text-center">{{tableData.tables[tableData.tableType].columns[0].name}}</th>
                                                    <th width="35%" class="border-top-0 text-center">{{tableData.tables[tableData.tableType].columns[1].name}}</th>
                                                </tr>
                                            </thead>

                                            <!-- Week days Occ Multiplier -->
                                            <thead v-if="isDaysOccMulti">
                                                <tr class="thead-size">
                                                    <th width="25%" class="border-0"></th>
                                                    <th width="auto" 
                                                        class="text-center border-0" 
                                                        :colspan="`${tableData.tables.daysOccMulti.columns.filter(item => !item.isDeleted).length}`">
                                                        <span class="font-weight-700">Occupation</span>
                                                    </th>
                                                </tr>
                                                
                                                <tr>
                                                    <th width="30%" class="text-left border-top-0">
                                                        <span class="font-weight-700">Days of Week</span>
                                                    </th>
                                                    <th v-for="col, index in tableData.tables.daysOccMulti.columns"
                                                        v-if="col.isDeleted === false"
                                                        :style="{ width: 70/tableData.tables.daysOccMulti.columns.filter(item => !item.isDeleted).length + '%' }" 
                                                        class="text-center border border-top-0">
                                                        
                                                        <div class="d-flex align-items-center justify-content-center">
                                                            <div class="d-flex align-items-center">
                                                                <input
                                                                    ref="inputField"
                                                                    @focus="selectText(index)"
                                                                    type="text"
                                                                    class="font-weight-700 fs-large input-col pr-0 text-right"
                                                                    v-model.number="col.val"
                                                                    @input="colLimitInput('daysOccMulti', index)"/>
                                                                    <div class="pl-1">%</div>
                                                            </div>
                                                            <i  class="ml-3 pr-0 fs-normal pointer fa fa fa-trash darker"
                                                                v-if="tableData.tables.daysOccMulti.columns.filter(item => !item.isDeleted).length !== 1"
                                                                @click="removeCol('daysOccMulti', null, index)"></i>
                                                        </div>
                                                    </th>

                                                    <th width="5%" class="text-center border border-top-0 border-right-0">
                                                        <button class="btn-add d-flex justify-content-center align-items-center" 
                                                                @click="addColumn('daysOccMulti', null)" 
                                                                v-if="addColValidation">

                                                            <i class="fs-normal fa fa-plus-circle dark"></i>
                                                        </button>
                                                    </th>
                                                </tr>
                                            </thead>

                                            <!-- Table Nº of Seats -->
                                            <tbody v-if="isSeatsNumber">
                                                <tr v-for="row, rowIndex in tableData.tables.seatsNumber.rows"
                                                    v-if="row.isDeleted === false">
                                                    <td class="col-3 p-2 border-right">
                                                        <div class="d-flex align-items-center">
                                                            <i  class="pl-0 pr-2 fs-normal pointer fa fa fa-trash darker"
                                                                :style="{visibility: tableData.tables.seatsNumber.rows.filter(item => !item.isDeleted).length === 1 ? 'hidden' : 'visible'}"
                                                                @click="removeRow('seatsNumber', rowIndex)"></i>
                                                            <v-select
                                                                v-model="row.channel.name"
                                                                autocomplete="no"
                                                                label="name"
                                                                :options="salesChannel"
                                                                @change="updateSalesChannel(row, $event)"
                                                                class="select-width">
                                                            </v-select>
                                                        </div>
                                                    </td>
                                                    <td class="border-right text-center">
                                                        <div class="d-flex justify-content-center">
                                                            <input 
                                                                class="text-center col-4" 
                                                                type="text"
                                                                v-model="row.seatsNumber"
                                                                @input="rowLimitInput('seatsNumber', rowIndex)"
                                                                @blur="rowLimitInput('seatsNumber', rowIndex, null, true)"
                                                                @focus="selectAllText($event)"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td class="border-right-0 text-center">
                                                        <div class="d-flex justify-content-center align-items-center">
                                                            <span class="closed-row p-2 border bold rounded">Close</span>
                                                        </div>
                                                    </td>
                                                </tr>

                                                <tr class="w-100 tr-size">
                                                    <td class="border-top">
                                                        <button class="btn-add d-flex justify-content-center align-items-center"
                                                                v-if="addRowValidation" 
                                                                @click="addRow(tableData.tableType, tableData.tableSubType)">

                                                            <i class="fs-normal fa fa-plus-circle dark"></i> Add
                                                        </button>
                                                    </td>                                              
                                                    <td class="border border-bottom-0 border-right-0" 
                                                        v-for="val in tableData.tables[tableData.tableType].columns"></td>
                                                </tr>
                                            </tbody>

                                            <!-- Table Channels Multiplier || Price Multiplier -->
                                            <tbody v-if="isChannelsMultiplier || isPriceMultiplier">
                                                <tr class="w-100 tr-size" 
                                                    v-for="row, rowIndex in tableData.tables[tableData.tableType][tableData.tableSubType].rows"
                                                    v-if="row.isDeleted === false">
                                                    <td class="col-3 p-2" v-if="isChannelsMultiplier">
                                                        <div class="d-flex align-items-center">
                                                            <i  class="pl-0 pr-2 fs-normal pointer fa fa fa-trash darker"
                                                                :style="{visibility: tableData.tables.channelsMultiplier[tableData.tableSubType].rows.length === 1 ? 'hidden' : 'visible'}"
                                                                @click="removeRow('channelsMultiplier', rowIndex, tableData.tableSubType)"></i>
                                                            <v-select
                                                                v-model="row.channel.name"
                                                                autocomplete="no"
                                                                :options="salesChannel"
                                                                label="name"
                                                                :loading="false"
                                                                @change="updateSalesChannel(row, $event)"
                                                                class="select-width">
                                                            </v-select>
                                                        </div>
                                                    </td>

                                                    <td class="border-right text-center" v-if="isPriceMultiplier">
                                                        <div class="d-flex justify-content-center">
                                                            <span class="col-5 input-row blocked-input d-flex align-items-center m-2 bold">
                                                                {{row.name}}
                                                                <i  class="fa fa-info-circle icons-tooltip" 
                                                                    v-tooltip="tableData.tables.priceMultiplier[tableData.tableSubType].tooltip">
                                                                </i>
                                                            </span>
                                                        </div>
                                                    </td>

                                                    <td class="text-center border-right border-left" 
                                                        v-for="valuesRow, valueIndex in row.values"
                                                        v-if="valuesRow.isDeleted === false">
                                                        <div v-if="tableData.tableSubType !== 'percentageCheck'" 
                                                            class="d-flex justify-content-center">
                                                            
                                                            <input 
                                                                class="text-center glued-right col-5 pr-0 multi-input" 
                                                                type="text"
                                                                v-model="valuesRow.multiVal"
                                                                @input="rowLimitInput(tableData.tableType, rowIndex, valueIndex)"
                                                                @blur="rowLimitInput(tableData.tableType, rowIndex, valueIndex, true)"
                                                                @focus="selectAllText($event)"
                                                            />
                                                            <div class="glued-left pl-0 d-flex align-items-center">%</div>
                                                        </div>

                                                        <ternary-checkbox 
                                                            v-if="tableData.tableSubType === 'percentageCheck'" 
                                                            v-model="valuesRow.ocVal"
                                                            classes="font-weight-700 fs-normal pr-0">
                                                        </ternary-checkbox>

                                                    </td>
                                                    <td class="text-center border-left">&nbsp;</td>
                                                </tr>

                                                <tr class="w-100 tr-size">
                                                    <td class="border-top">
                                                        <button class="btn-add d-flex justify-content-center align-items-center"
                                                                v-if="addRowValidation" 
                                                                @click="addRow(tableData.tableType, tableData.tableSubType)">

                                                            <i class="fs-normal fa fa-plus-circle dark"></i> Add
                                                        </button>
                                                    </td>
                                                    <td class="border border-bottom-0 border-right-0" 
                                                        v-for="val in tableData.tables[tableData.tableType][tableData.tableSubType].columns"
                                                        v-if="val.isDeleted === false"></td>
                                                    <td class="border-left border-top">&nbsp;</td>
                                                </tr>
                                            </tbody>
                                                
                                             <!-- Week days Occ Multiplier -->
                                            <tbody v-if="isDaysOccMulti">
                                                <tr class="w-100 tr-size"
                                                    v-for="row, rowIndex in tableData.tables.daysOccMulti.rows"
                                                    v-if="row.isDeleted === false">
                                                    <td class="p-2 d-flex justify-content-center">
                                                        <div class="d-flex align-items-center">
                                                            <i  class="pl-0 pr-2 fs-normal pointer fa fa fa-trash darker"
                                                                :style="{visibility: tableData.tables.daysOccMulti.rows.length === 1 ? 'hidden' : 'visible'}"
                                                                @click="removeRow('daysOccMulti', rowIndex, null)"></i>
                                                            <v-select
                                                                v-model="row.day.val"
                                                                label="val"
                                                                autocomplete="no"
                                                                :options="availableFullNameDays"
                                                                class="days-week-input"
                                                                @input="updateWeekDay(row, $event)">
                                                            </v-select>
                                                        </div>
                                                    </td>
                                                    <td class="text-center border-right border-left" 
                                                        v-for="valuesRow, valueIndex in row.values"
                                                        v-if="valuesRow.isDeleted === false">
                                                        <div class="d-flex justify-content-center">
                                                            <input 
                                                                class="text-center glued-right col-5 pr-0 multi-input" 
                                                                type="text"
                                                                v-model="valuesRow.multiVal"
                                                                @input="rowLimitInput('daysOccMulti', rowIndex, valueIndex)"
                                                                @blur="rowLimitInput('daysOccMulti', rowIndex, valueIndex, true)"
                                                                @focus="selectAllText($event)"
                                                            />
                                                            <div class="glued-left pl-0 d-flex align-items-center">%</div>
                                                        </div>
                                                    </td>
                                                    <td class="text-center border-left">&nbsp;</td>
                                                </tr>

                                                <tr class="w-100 tr-size">
                                                    <td class="border-top">
                                                        <button class="btn-add d-flex justify-content-center align-items-center"
                                                                v-if="addRowValidation" 
                                                                @click="addRow(tableData.tableType, tableData.tableSubType)">

                                                            <i class="fs-normal fa fa-plus-circle dark"></i> Add
                                                        </button>
                                                    </td>
                                                    <td class="border border-bottom-0 border-right-0" 
                                                        v-for="val in tableData.tables[tableData.tableType].columns"
                                                        v-if="val.isDeleted === false"></td>
                                                    <td class="border-left border-top">&nbsp;</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        
                                        <div v-else class="pt-5 d-flex justify-content-center">
                                            <!-- Desenvolver aqui uma progress bar em função das escolhas das variaveis sendo progressivo -->
                                            <span class="font-weight-700 fs-large dark bold">You must choose first all variables!</span>
                                        </div>
                                    </div>
                                        
                                    <div class="d-flex flex-column mt-5"
                                         v-if="tableData.tableSubType === 'percentageCheck'">
                                        
                                        <div class="mb-1">
                                            <label class="bold darkest fs-normal">Legend:</label>
                                        </div>

                                        <div class="d-flex">
                                            <div class="d-flex flex-column align-items-center mr-4">
                                                <label>Close channel</label>

                                                <input 
                                                    type="checkbox"
                                                    :checked="false"
                                                    :disabled="false"
                                                    classes="font-weight-700 fs-normal pr-0"
                                                    @click.prevent="$event.preventDefault()">
                                                </input>
                                            </div>
                                            <div class="d-flex flex-column align-items-center mr-4">
                                                <label>Open channel</label>

                                                <input
                                                    type="checkbox"
                                                    :checked="true"
                                                    :disabled="false"
                                                    classes="font-weight-700 fs-normal pr-0"
                                                    @click.prevent="$event.preventDefault()">
                                                </input>
                                            </div>
                                            <div class="d-flex flex-column align-items-center mr-4">
                                                <label>Does nothing</label>

                                                <input
                                                    type="checkbox"
                                                    :indeterminate.prop="true"
                                                    :disabled="false"
                                                    classes="font-weight-700 fs-normal pr-0"
                                                    @click.prevent="$event.preventDefault()">
                                                </input>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <h5 class="">What should be done if there are cancellations?</h5>
                                
                                <div class="row mt-4">
                                    <div class="col-xl-5 col-lg-7 col-sm-12 mb-2 d-flex">
                                        <v-select
                                            v-model="cancelations.value"
                                            autocomplete="no"
                                            :options="cancelations.options">
                                        </v-select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>  
        </div>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import Multiselect from 'vue-multiselect'
    import extendedVSelect from '@/extends/vue-select';
    import { required, minLength } from "vuelidate/lib/validators";
    import { rulesMixin } from '@/mixins/rules';
    import TernaryCheckbox from '../Checkbox';

    export default {
         data: function() {
            return {
                tableData: {
                    tableType: '',
                    tableSubType: '',
                    variables: {
                        row: '',
                        column: '',
                        action: ''
                    },
                    tables: {
                        priceMultiplier: {
                            moreThen: {
                                name: "Occupation (More then)",
                                tooltip: 'Value configured on the product',
                                columns: [{ val: 20, isDeleted: false }, { val: 40, isDeleted: false }, { val: 60, isDeleted: false }],
                                rows: [
                                    { name: 'Product Value', values: [{ multiVal: 0, isDeleted: false }, { multiVal: 0, isDeleted: false }, { multiVal: 0, isDeleted: false }], isDeleted: false },
                                ]
                            },
                            lessThen: {
                                name: "Occupation (Less then)",
                                tooltip: 'Value configured on the product',
                                columns: [{ val: 20, isDeleted: false }, { val: 40, isDeleted: false }, { val: 60, isDeleted: false }],
                                rows: [
                                    { name: 'Product Value', values: [{ multiVal: 0, isDeleted: false }, { multiVal: 0, isDeleted: false }, { multiVal: 0, isDeleted: false }], isDeleted: false },
                                ]
                            },
                        },
                        seatsNumber: {
                            columns: [
                                { name: "Nº of Seats" },
                                { name: "Action" }
                            ],
                            rows: []
                        },
                        channelsMultiplier: {
                            percentageCheck: {
                                columns: [{ val: 20, isDeleted: false }, { val: 40, isDeleted: false }, { val: 60, isDeleted: false }],
                                rows: []
                            },
                            percentagePerInput: {
                                columns: [{ val: 20, isDeleted: false }, { val: 40, isDeleted: false }, { val: 60, isDeleted: false }],
                                rows: []
                            },
                            weekDaysPerInput: {
                                columns: [{ day: { val: 'Mon', id: 1}, isDeleted: false }, { day: { val: 'Tue', id: 2}, isDeleted: false }, { day: { val: 'Wed', id: 3}, isDeleted: false }],
                                rows: []
                            }
                        },
                        daysOccMulti: {
                            columns: [{ val: 20, isDeleted: false }, { val: 40, isDeleted: false }, { val: 60, isDeleted: false }],
                            rows: [
                                { day: { val: 'Monday', id: 1}, values: [{ multiVal: 0, isDeleted: false }, { multiVal: 0, isDeleted: false }, { multiVal: 0, isDeleted: false }], isDeleted: false },
                                { day: { val: 'Tuesday', id: 2}, values: [{ multiVal: 0, isDeleted: false }, { multiVal: 0, isDeleted: false }, { multiVal: 0, isDeleted: false }], isDeleted: false }
                            ]
                        },
                    }
                },
                tableLine: '',
                tableColumn: '',
                ruleName: '',
                rowOptions: ['Sales Channels', 'Base Price', 'Days of Week'],
                columnOptions: [],
                actionOptions: [],
                weekOptions: {
                    fullName: [],
                    abbreviations: []
                },
                cancelations: {
                    value: 'Maintain rule',
                    options: ['Maintain rule', 'Revert rule']
                },
                selectedRule: null,
                isEdit: false,
                weekDays: {
                    fullName: [
                        {val: 'Monday', id: 1}, 
                        {val: 'Tuesday', id: 2},
                        {val: 'Wednesday', id: 3},
                        {val: 'Thursday', id: 4},
                        {val: 'Friday', id: 5}, 
                        {val: 'Saturday', id: 6}, 
                        {val: 'Sunday', id: 0}
                    ],
                    abbreviations: [
                        {val: 'Mon', id: 1},
                        {val: 'Tue', id: 2},
                        {val: 'Wed', id: 3}, 
                        {val: 'Thu', id: 4}, 
                        {val: 'Fri', id: 5}, 
                        {val: 'Sat', id: 6}, 
                        {val: 'Sun', id: 0}
                    ]
                },
                selectedRuleClone: null,
                actionOptionsMap: {
                    'Sales Channels': {
                        'Occupation': ['Open/Close', 'Price Increase'],
                        'Number of Seats': ['Close'],
                        'Days of Week': ['Price Increase']
                    },
                    'Base Price': {
                        'Occupation (More then)': ['Price Increase'],
                        'Occupation (Less then)': ['Price Increase']
                    },
                    'Days of Week': {
                        'Occupation': ['Price Increase']
                    }
                },
                columnOptionsMap: {
                    'Sales Channels': ['Occupation', 'Number of Seats', 'Days of Week'],
                    'Base Price': ['Occupation (More then)', 'Occupation (Less then)'],
                    'Days of Week': ['Occupation']
                },
                tableMappings: {
                    'Sales Channels': {
                        'Occupation': {
                            'Price Increase': { tableType: 'channelsMultiplier', tableSubType: 'percentagePerInput' },
                            'Open/Close': { tableType: 'channelsMultiplier', tableSubType: 'percentageCheck' }
                        },
                        'Days of Week': { tableType: 'channelsMultiplier', tableSubType: 'weekDaysPerInput' },
                        'Number of Seats': { tableType: 'seatsNumber', tableSubType: 'seatsNumber' }
                    },
                    'Base Price': {
                        'Occupation (More then)': { tableType: 'priceMultiplier', tableSubType: 'moreThen' },
                        'Occupation (Less then)': { tableType: 'priceMultiplier', tableSubType: 'lessThen' },
                    },
                    'Days of Week': { tableType: 'daysOccMulti', tableSubType: 'daysOccMulti' }
                },
                isReady: false,
                specialCharacters: "<>|\\#$%/()=*+-_.:;,€ºª"
            }
        },
        mixins: [
            rulesMixin,
        ],
        validations: {
            ruleName: {
                required,
                minLength: minLength(5),
                validCharacters: function (val) {
                    return this.validCharacters(val);
                }
            },
            tableData: {
                variables: {
                    row: {
                        required,
                    },
                    column: {
                        required,
                    },
                    action: {
                        required,
                    },
                }
            }
        },
        beforeRouteEnter: function(to, from, next) {
            next(vm => {
                if (from.path === '/') {
                    vm.$router.push('/rules');
                    vm.clearSelectedItemRule();
                }  else  {
                    vm.isReady = true;
                    vm.initializeComponent();
                }
            });
        },
        destroyed() {
            if (this.isEdit) this.clearSelectedItemRule()
        },
        computed: {
            ...mapGetters([
                "getSalesChannels",
                'getRules',
                'getSelectedRuleItem',
                'getNewGroupRule',
                'account',
            ]),
            isEditType() {
                if (this.getSelectedRuleItem !== null) {
                    this.selectedRule = this.getSelectedRuleItem.item;
                    this.selectedRuleClone = JSON.parse(JSON.stringify(this.getSelectedRuleItem.item));
                }
                
                return this.isEdit = this.selectedRule !== null ? true : false;
            },
            salesChannel() {
                const salesChannels = this.getSalesChannels;
                const tableType = this.tableData.tableType;
                const tableSubType = this.tableData.tableSubType;
                const tables = this.tableData.tables;
                let usedChannels;

                if (tableType === 'channelsMultiplier') {
                    usedChannels = new Set(tables[tableType][tableSubType].rows.filter(item => !item.isDeleted).map(row => row.channel.name))
                } else if (tableType === 'seatsNumber') {
                    usedChannels = new Set(tables[tableType].rows.filter(item => !item.isDeleted).map(row => row.channel.name))
                }

                return  salesChannels.filter(channel => !usedChannels.has(channel.name)).map(channel => {
                            return { name: channel.name, id: channel.id} 
                        });
            },
            isChannelsMultiplier() {
                return this.tableData.tableType === 'channelsMultiplier';
            },
            isSeatsNumber() {
                return this.tableData.tableType === 'seatsNumber';
            },
            isPriceMultiplier() {
                return this.tableData.tableType === 'priceMultiplier';
            },
            isDaysOccMulti() {
                return this.tableData.tableType === 'daysOccMulti';
            },
            isTableReady() {
                return this.tableData.tableType && this.tableData.tableSubType;
            },
            addColValidation() {
                if (this.tableData.tableType === 'daysOccMulti') {
                    return this.tableData.tables.daysOccMulti.columns.filter(item => !item.isDeleted).length < 10
                } else {
                    return this.tableData.tableSubType !== 'weekDaysPerInput' 
                                ? this.tableData.tables[this.tableData.tableType][this.tableData.tableSubType].columns.filter(item => !item.isDeleted).length < 10 
                                : this.tableData.tables[this.tableData.tableType][this.tableData.tableSubType].columns.filter(item => !item.isDeleted).length < 7
                }
            },
            addRowValidation() {
                const { tableType, tables, tableSubType } = this.tableData;
                const getActiveRowsCount = (table) => table.rows.filter(item => !item.isDeleted).length;
                const validations = {
                    daysOccMulti: () => getActiveRowsCount(tables.daysOccMulti) < 7,
                    seatsNumber: () => this.getSalesChannels.length > getActiveRowsCount(tables.seatsNumber),
                    channelsMultiplier: () => this.getSalesChannels.length > getActiveRowsCount(tables.channelsMultiplier[tableSubType]),
                    priceMultiplier: () => false
                };

                if (validations[tableType]) {
                    return validations[tableType]();
                }

                return false;
            },
            availableFullNameDays() {
                const usedDays = new Set(this.tableData.tables.daysOccMulti.rows.filter(item => !item.isDeleted).map(row => row.day.val));
                
                return this.weekDays.fullName.filter(day => !usedDays.has(day.val));
            },
            availableAbbreviationDays() {
                const usedDays = new Set(this.tableData.tables.channelsMultiplier.weekDaysPerInput.columns.filter(item => !item.isDeleted).map(column => column.day.val));
                
                return this.weekDays.abbreviations.filter(day => !usedDays.has(day.val));
            },
            validateIncreaseMsg() {
                return this.tableData.tableType === 'priceMultiplier' || this.tableData.tableType === 'daysOccMulti';
            },
            validateOverrideMsg() {
                if (this.tableData.tableType === 'channelsMultiplier') {
                    return this.tableData.tableSubType === 'percentagePerInput' || this.tableData.tableSubType === 'weekDaysPerInput';
                }
                
                return false;
            }
        },
        methods: {
            ...mapActions([
                'clearSelectedItemRule',
                'createRule',
                'updateRule',
                'updateEditedRuleOrGroupToSave',
                'openModal',
                'startFullScreenLoading',
                'stopFullScreenLoading',
                'downloadRules'
            ]),
            validCharacters: function(val) {
                const allowedCharacters = /^[a-zA-Z0-9áàâãéêíóôõúçÁÀÂÃÉÊÍÓÔÕÚÇ\s<>|\\#$%\/()=*+\-_.:;,€ºª]*$/;
                
                return allowedCharacters.test(val);
            },
            initializeComponent() {
                if (this.isEditType) {
                    this.initEditRule()
                }
            },
            selectText(idx) {
                if (this.$refs.inputField && this.$refs.inputField[idx]) {
                    this.$refs.inputField[idx].select();
                }
            },
            initEditRule() {
                const ruleMap = {
                    '1-1-3': {
                        ruleDefinition: 'Base Price | Occupation (More then) | Price Increase', 
                        tableType: 'priceMultiplier', 
                        tableSubType: 'moreThen'
                        },
                    '1-2-3': {
                        ruleDefinition: 'Base Price | Occupation (Less then) | Price Increase',
                        tableType: 'priceMultiplier', 
                        tableSubType: 'lessThen'
                        },
                    '2-1-1': {
                        ruleDefinition: 'Sales Channels | Occupation | Open/Close',
                        tableType: 'channelsMultiplier', 
                        tableSubType: 'percentageCheck'
                        },
                    '2-1-3': {
                        ruleDefinition: 'Sales Channels | Occupation | Price Increase',
                        tableType: 'channelsMultiplier', 
                        tableSubType: 'percentagePerInput'
                        },
                    '2-4-2': {
                        ruleDefinition: 'Sales Channels | Number of Seats | Close',
                        tableType: 'seatsNumber', 
                        tableSubType: null
                        },
                    '2-3-3': {
                        ruleDefinition: 'Sales Channels | Days of Week | Price Increase',
                        tableType: 'channelsMultiplier', 
                        tableSubType: 'weekDaysPerInput'
                        },
                    '3-1-3': {
                        ruleDefinition: 'Days of Week | Occupation | Price Increase',
                        tableType: 'daysOccMulti', 
                        tableSubType: null
                        },
                };

                const key = `${this.selectedRule.targetVariableType}-${this.selectedRule.conditionType}-${this.selectedRule.actionType}`;
                const initData = ruleMap[key].ruleDefinition.split(' | ')
                const tableTypeData = ruleMap[key].tableType
                const tableSubTypeData = ruleMap[key].tableSubType

                this.ruleName = this.selectedRule.name;
                this.cancelations.value = this.selectedRule.behaviorOnCancellation === 2 ? 'Maintain rule' : 'Revert rule';
                
                this.tableData.variables.row = initData[0]
                this.tableData.variables.column = initData[1]
                this.tableData.variables.action = initData[2]
                this.tableData.tableType = tableTypeData
                this.tableData.tableSubType = tableSubTypeData
                let columns = [];
                let rows = [];
                
                if (tableSubTypeData !== null) {
                    // Columns
                    if(tableTypeData === 'priceMultiplier') {
                        this.selectedRule.priceRuleTargets[0].priceRuleConditions.forEach(ruleInfo => {
                            columns.push({
                                val: ruleInfo.conditionValue,
                                isDeleted: ruleInfo.isDeleted
                            })
                        })
                    } else if (tableTypeData === 'channelsMultiplier') {
                        if (tableSubTypeData !== 'weekDaysPerInput') {
                            this.selectedRule.priceRuleTargets[0].priceRuleConditions.forEach(ruleInfo => {
                                columns.push({
                                    val: ruleInfo.conditionValue,
                                    isDeleted: ruleInfo.isDeleted,
                                })
                            })
                        } else {
                            this.selectedRule.priceRuleTargets[0].priceRuleConditions.forEach(ruleInfo => {
                                columns.push({
                                    day: this.weekDays.abbreviations.find(name => name.id === ruleInfo.conditionDayOfWeek),
                                    isDeleted: ruleInfo.isDeleted,
                                })
                            })
                        }
                    }
                    
                    // Rows
                    this.selectedRule.priceRuleTargets.map(info => {
                        if(tableTypeData === 'priceMultiplier') {
                            rows.push({
                                name: 'Product Value',
                                values: info.priceRuleConditions.map(ruleInfo => {
                                    return {
                                        multiVal: ruleInfo.multiplyValue,
                                        isDeleted: ruleInfo.isDeleted,
                                    }
                                }),
                                isDeleted: info.isDeleted
                            })
                        } else if (tableTypeData === 'channelsMultiplier') {
                            if (tableSubTypeData !== 'percentageCheck') {
                                rows.push({
                                    channel: {name: info.channel.name, id: info.channel.id},
                                    values: info.priceRuleConditions.map(ruleInfo => {
                                        return {
                                            multiVal: ruleInfo.multiplyValue,
                                            isDeleted: ruleInfo.isDeleted,
                                        }
                                    }),
                                    isDeleted: info.isDeleted
                                })
                            } else {
                                rows.push({
                                    channel: {name: info.channel.name, id: info.channel.id},
                                    values: info.priceRuleConditions.map(ruleInfo => {
                                        return {
                                            ocVal: ruleInfo.openCloseValue === 2 ? null : ruleInfo.openCloseValue === 1 ? true : false,
                                            isDeleted: ruleInfo.isDeleted,
                                        }
                                    }),
                                    isDeleted: info.isDeleted
                                })
                            }
                        } 
                    })

                    this.tableData.tables[tableTypeData][tableSubTypeData].columns = columns;
                    this.tableData.tables[tableTypeData][tableSubTypeData].rows = rows;

                } else {
                    // Columns
                    if (tableTypeData === 'daysOccMulti') {
                        this.selectedRule.priceRuleTargets[0].priceRuleConditions.map(ruleInfo => {
                            columns.push({
                                val: ruleInfo.conditionValue,
                                isDeleted: ruleInfo.isDeleted,
                            })
                        }) 
                    }
                    
                    // Rows
                    this.selectedRule.priceRuleTargets.map(info => {
                        if (tableTypeData === 'daysOccMulti') {
                            rows.push({
                                day: this.weekDays.fullName.find(name => name.id === info.dayOfWeek),
                                values: info.priceRuleConditions.map(ruleInfo => {
                                    return {
                                        multiVal: ruleInfo.multiplyValue,
                                        isDeleted: ruleInfo.isDeleted,
                                    }
                                }),
                                isDeleted: info.isDeleted,
                            })
                        } else if (tableTypeData === 'seatsNumber') {
                            rows.push({
                                channel: {name: info.channel.name, id: info.channel.id},
                                seatsNumber: info.priceRuleConditions[0].conditionValue,
                                isDeleted: info.isDeleted,
                            })
                        }
                    })

                    this.tableData.tables[tableTypeData].columns = columns.length > 0 ? columns : this.tableData.tables[tableTypeData].columns;
                    this.tableData.tables[tableTypeData].rows = rows;
                }

                this.initTable();
            },
            updateSalesChannel(row, event) {
                row.channel.id = typeof event === 'object' ? event.id : row.channel.id;
                row.channel.name = typeof event === 'object' ? event.name : row.channel.name;
            },
            updateWeekDay(data, event){
                data.day.id = typeof event === 'object' ? event.id : data.day.id;
                data.day.val = typeof event === 'object' ? event.val : data.day.val;
            },
            colLimitInput(tableType, colIndex) {
                const limitToDigits = (value, digits) => parseInt(value.toString().slice(0, digits)) || 1;
                const tables = this.tableData.tables[tableType];
                const tableSubType = this.tableData.tableSubType;
                let value;

                if (tableType === 'daysOccMulti') {
                    value = tables.columns[colIndex].val;
                    tables.columns[colIndex].val = limitToDigits(value, 3);
                } else if (tableType === 'channelsMultiplier' && tableSubType !== 'weekDaysPerInput' || tableType === 'priceMultiplier') {
                    value = tables[tableSubType].columns[colIndex].val;
                    tables[tableSubType].columns[colIndex].val = limitToDigits(value, 3);
                }
            },
            selectAllText(event) {
                event.target.select();
            },
            rowLimitInput: function(tableType, rowIndex, valueIndex = null, isBlurEvent = false) {
                const limitToDigits = (value, digits) => {
                    let valueStr = value.toString().replace(/\D/g, '');

                    // Return empty string if input is empty
                    if (!valueStr) return '';

                    // Remove left zeros live
                    if (valueStr.length > 1) {
                        valueStr = valueStr.replace(/^0+/, '');
                    }
                    // Digits limitor
                    valueStr = valueStr.slice(0, digits);

                    return valueStr;
                };

                const tables = this.tableData.tables[tableType];
                const tableSubType = this.tableData.tableSubType;
                let value;

                if (tableType === 'seatsNumber' ) {
                    value = tables.rows[rowIndex].seatsNumber;
                    tables.rows[rowIndex].seatsNumber = limitToDigits(value, 4);

                    // Set minimum value in blur event
                    if (isBlurEvent && (tables.rows[rowIndex].seatsNumber === '' || isNaN(tables.rows[rowIndex].seatsNumber))) {
                        tables.rows[rowIndex].seatsNumber = 1;
                    }
                } else if (tableType === 'daysOccMulti') {
                    value = tables.rows[rowIndex].values[valueIndex].multiVal;
                    tables.rows[rowIndex].values[valueIndex].multiVal = limitToDigits(value, 3);

                    // Set minimum value in blur event
                    if (isBlurEvent && (tables.rows[rowIndex].values[valueIndex].multiVal === '' || isNaN(tables.rows[rowIndex].values[valueIndex].multiVal))) {
                        tables.rows[rowIndex].values[valueIndex].multiVal = 0;
                    }
                } else if (tableType === 'channelsMultiplier' || tableType === 'priceMultiplier') {
                    value = tables[tableSubType].rows[rowIndex].values[valueIndex].multiVal;
                    tables[tableSubType].rows[rowIndex].values[valueIndex].multiVal = limitToDigits(value, 3);

                    // Set minimum value in blur event
                    if (isBlurEvent && (tables[tableSubType].rows[rowIndex].values[valueIndex].multiVal === '' || isNaN(tables[tableSubType].rows[rowIndex].values[valueIndex].multiVal))) {
                        tables[tableSubType].rows[rowIndex].values[valueIndex].multiVal = 0;
                    }
                }
            },
            initTable: function() {
                const { row, column, action } = this.tableData.variables;
                const lineMapping = this.tableMappings[row];
                const tableData = this.tableData;

                if (lineMapping) {
                    const columnMapping = lineMapping[column] || lineMapping;
                    const actionMapping = columnMapping[action] || columnMapping;

                    tableData.tableType = actionMapping.tableType;
                    tableData.tableSubType = actionMapping.tableSubType;
                }

                if (!this.isEdit && tableData.tableType && tableData.tableSubType) {
                    let isNewTable = false;

                    const tableType = tableData.tableType;
                    const tableSubType = tableData.tableSubType;
                    const tables = this.tableData.tables;

                    if (tableType === 'seatsNumber' || tableType === 'daysOccMulti') {
                        isNewTable = tables[tableType].rows.length === 0;
                    } else if (tableType !== 'priceMultiplier') {
                        isNewTable = tables[tableType][tableSubType].rows.length === 0;
                    }

                    if (isNewTable) {
                        this.addRow(tableType, tableSubType);
                    }
                }
            },
            getFinalTable: function() {
                const { tableType, tableSubType, tables } = this.tableData;
                if (tableType === 'priceMultiplier' || tableType === 'channelsMultiplier') {
                    return tables[tableType][tableSubType];
                } else {
                    return tables[tableType];
                }
            },
            getFinalRule() {
                const { tableType, tableSubType } = this.tableData;
                let targetVarType;
                let conditionType;
                let actionType;
                let priceRuleTarget;

                if (tableType === 'channelsMultiplier') {
                    targetVarType = 2 // Channel
                    if (tableSubType === 'percentageCheck') {
                        conditionType = 1 // Occupancin bigger than
                        actionType = 1 // open/close
                        priceRuleTarget = this.tableData.tables[tableType][tableSubType].rows.map(row => {
                            return {
                                "channelId": row.channel.id,
                                "isDeleted": row.isDeleted,
                                "priceRuleConditions": this.tableData.tables[tableType][tableSubType].columns.reduce((acc, col, idx) => {
                                    if(!(row.values[idx].isDeleted && row.values[idx].isNew)) {
                                        acc.push({
                                            "conditionValue": col.val,
                                            "openCloseValue": row.values[idx].ocVal === null ? 2 : row.values[idx].ocVal === true ? 1 : 0,
                                            "isDeleted": col.isDeleted,
                                        })
                                    } 

                                    return acc
                                }, [])
                            }
                        })
                    } else if (tableSubType === 'percentagePerInput') {
                        conditionType = 1 // Occupancin bigger than
                        actionType = 3 // Price Increase / Multiplier
                        priceRuleTarget = this.tableData.tables[tableType][tableSubType].rows.map(row => {
                            return {
                                "channelId": row.channel.id,
                                "isDeleted": row.isDeleted,
                                "priceRuleConditions": this.tableData.tables[tableType][tableSubType].columns.reduce((acc, col, idx) => {
                                    if(!(row.values[idx].isDeleted && row.values[idx].isNew)) {
                                        acc.push({
                                            "conditionValue": col.val,
                                            "multiplyValue": row.values[idx].multiVal,
                                            "isDeleted": col.isDeleted,
                                        })
                                    } 

                                    return acc
                                }, [])
                            }
                        })
                    } else if (tableSubType === 'weekDaysPerInput') {
                        conditionType = 3 // Days of week
                        actionType = 3 // Price Increase / Multiplier 
                        priceRuleTarget = this.tableData.tables[tableType][tableSubType].rows.map(row => {
                            return {
                                "channelId": row.channel.id,
                                "isDeleted": row.isDeleted,
                                "priceRuleConditions": this.tableData.tables[tableType][tableSubType].columns.reduce((acc, col, idx) => {
                                    if(!(row.values[idx].isDeleted && row.values[idx].isNew)) {
                                        acc.push({
                                            "conditionDayOfWeek": col.day.id,
                                            "multiplyValue": row.values[idx].multiVal,
                                            "isDeleted": col.isDeleted,
                                        })
                                    } 

                                    return acc
                                }, [])
                            }
                        })
                    }
                } else if (tableType === 'priceMultiplier') {
                    targetVarType = 1 // Event
                    conditionType = tableSubType === 'moreThen' ? 1 : 2 // More then - 1 / Less then - 2
                    actionType = 3 // Price Increase / Multiplier
                    priceRuleTarget = this.tableData.tables[tableType][tableSubType].rows.map(row => {
                        return {
                            "basePrice": true,
                            "isDeleted": row.isDeleted,
                            "priceRuleConditions": this.tableData.tables[tableType][tableSubType].columns.map((col, idx) => {
                                return {
                                    "conditionValue": col.val,
                                    "multiplyValue": row.values[idx].multiVal,
                                    "isDeleted": col.isDeleted,
                                }
                            })
                        }
                    })
                } else if (tableType === 'seatsNumber') {
                    targetVarType = 2 // Channel
                    conditionType = 4 // Quantity Sold
                    actionType = 2 // Close
                    priceRuleTarget = this.tableData.tables[tableType].rows.map(row => {
                        return {
                            "channelId": row.channel.id,
                            "isDeleted": row.isDeleted,
                            "priceRuleConditions": [{
                                    "conditionValue": row.seatsNumber,
                                    "openCloseValue": 0,
                                }]
                        }
                    })
                } else if (tableType === 'daysOccMulti') {
                    targetVarType = 3 // Days of Week
                    conditionType = 1 // Occupancy bigger than
                    actionType = 3 // Price Increase / Multiplier
                    priceRuleTarget = this.tableData.tables[tableType].rows.map(row => {
                        return {
                            "dayOfWeek": row.day.id,
                            "isDeleted": row.isDeleted,
                            "priceRuleConditions": this.tableData.tables[tableType].columns.reduce((acc, col, idx) => {
                                    if(!(row.values[idx].isDeleted && row.values[idx].isNew)) {
                                        acc.push({
                                            "conditionValue": col.val,
                                            "multiplyValue": row.values[idx].multiVal,
                                            "isDeleted": col.isDeleted,
                                        })
                                    } 

                                    return acc
                                }, [])
                        }
                    })
                }
                
                const finalRule = {
                    "name": this.ruleName,
                    "targetVariableType": targetVarType,
                    "conditionType": conditionType,
                    "actionType": actionType,
                    "behaviorOnCancellation": this.cancelations.value === 'Maintain rule' ? 2 : 1,
                    "supplierId": this.account.supplierId,
                    "priceRuleTargets": priceRuleTarget
                }

                return finalRule;
            },
            btnCancel: function() {
                this.$router.push({ name: 'rules' });
            },
            btnSave: function() {
                const saveRule = (rule) => {
                    if (this.isEdit) {
                        const self = this;
                        const finalEditRule = {
                            ...this.getSelectedRuleItem.item, 
                            ...rule,
                            priceRuleTargets: rule.priceRuleTargets.map((targets, targetIdx) => {
                                return {
                                    ...this.getSelectedRuleItem.item.priceRuleTargets[targetIdx],
                                    ...targets,
                                    priceRuleConditions: targets.priceRuleConditions.map((conditions,conditionIdx) => {
                                        if (self.getSelectedRuleItem.item.priceRuleTargets[targetIdx] !== undefined && self.getSelectedRuleItem.item.priceRuleTargets[targetIdx].priceRuleConditions[conditionIdx] !== undefined) {
                                            return {
                                                ...self.getSelectedRuleItem.item.priceRuleTargets[targetIdx].priceRuleConditions[conditionIdx],
                                                ...conditions,
                                            }
                                        } else {
                                            return {
                                                ...conditions,
                                            }
                                        }
                                    })
                                }
                            })
                        }

                        if (finalEditRule.hasProductGroupAssossiation && 
                            !_.isEqual(finalEditRule.priceRuleTargets, self.selectedRuleClone.priceRuleTargets) || 
                            !_.isEqual(finalEditRule.behaviorOnCancellation, self.selectedRuleClone.behaviorOnCancellation)) {
                            
                            this.updateEditedRuleOrGroupToSave({rule: finalEditRule, type: 'rule'});
                            this.openModal('RuleSaveChanges');
                        } else {
                            this.initSavingProcess();

                            this.updateRule(finalEditRule).then(suc => {
                                if (suc) {
                                    this.$toasted.success('Successfully saved', { duration: 2000 });
                                } else {
                                    this.$toasted.error('An error occurred', { duration: 2000 });
                                }

                                this.finalizeAndRedirect();
                            }).catch(error => {
                                this.errorHandling(error);
                            })
                        }
                    } else {
                        this.initSavingProcess();

                        this.createRule(rule).then(suc => {
                            if (suc) {
                                this.$toasted.success('Successfully saved', { duration: 2000 });
                            } else {
                                this.$toasted.error('An error occurred', { duration: 2000 });
                            }

                            this.finalizeAndRedirect();
                        }).catch(error => {
                           this.errorHandling(error);
                        })
                    }
                };

                const newRule = this.getFinalRule()
                saveRule(newRule);
            },
            errorHandling: function (error) {
                this.$toasted.error('An unexpected error occurred', { duration: 2000 });
                this.stopFullScreenLoading();
            },
            finalizeAndRedirect: function() {
                if (this.getNewGroupRule) {
                    this.downloadRules().then(() => this.$router.push({ name: 'pluralo.rulesGroup.new' }))
                } else {
                    this.$router.push({ name: 'rules' });
                }

                this.stopFullScreenLoading();
            },
            initSavingProcess: function() {
                this.$toasted.info('Saving', { duration: 2000 });
                this.startFullScreenLoading();
            },
            updateColumn: function(val) {
                this.tableData.variables.row = val;
                this.tableLine = val;
                this.columnOptions = this.columnOptionsMap[val] || [];

                if (this.columnOptions.length === 1) {
                    this.tableData.variables.column = this.columnOptions[0];
                    this.updateAction(this.tableData.variables.column);
                    this.initTable();
                } else {
                    this.tableData.variables.column = '';
                    this.tableData.tableSubType = '';
                }
            },
            updateAction: function(val) {
                const lineVal = this.tableData.variables.row;

                this.tableData.variables.action = !this.isEdit ? '' : this.tableData.variables.action;
                this.tableColumn = val;
                this.actionOptions = (this.actionOptionsMap[lineVal] && this.actionOptionsMap[lineVal][val]) || [];

                if (this.actionOptions.length === 1) {
                    this.tableData.variables.action = this.actionOptions[0];
                    this.initTable();
                } else {
                    this.tableData.variables.action = !this.isEdit ? '' : this.tableData.variables.action;
                    this.tableData.tableSubType = !this.isEdit ? '' : this.tableData.tableSubType;
                }
            },
            addRow: function (tableType, tableSubType) {
                const tables = this.tableData.tables;

                if (tableType === 'seatsNumber') {
                    tables[tableType].rows.push({
                        channel: this.salesChannel[0],
                        seatsNumber: 1,
                        isDeleted: false
                    });
                } else if (tableType === 'channelsMultiplier') {
                    const values = tables[tableType][tableSubType].columns.map(col => {
                        return tableSubType === 'percentageCheck' ? { ocVal: null, isDeleted: col.isDeleted, isNew: true } : { multiVal: 0, isDeleted: col.isDeleted, isNew: true }
                    })

                    tables[tableType][tableSubType].rows.push({
                        channel: this.salesChannel[0],
                        values: values,
                        isDeleted: false
                    });
                } else if (tableType === 'daysOccMulti') {
                    const values = tables[tableType].columns.map(col => ({ multiVal: 0, isDeleted: col.isDeleted, isNew: true }))

                    tables[tableType].rows.push({
                        day: {...this.availableFullNameDays[0]},
                        values: values,
                        isDeleted: false
                    });
                }
            },
            addColumn: function (tableType, tableSubType) {
                const tables = this.tableData.tables;

                if (tableType === 'channelsMultiplier' || tableType === 'priceMultiplier') {
                    const columnValue = tableSubType !== 'weekDaysPerInput' 
                                ? {val: 10, isDeleted: false} 
                                : {day: {...this.availableAbbreviationDays[0]}, isDeleted: false};

                    tables[tableType][tableSubType].columns.push(columnValue);
                    tables[tableType][tableSubType].rows.forEach(row => row.values.push(tableSubType === 'percentageCheck' ? { ocVal: null, isDeleted: false } : { multiVal: 0, isDeleted: false }));
                } else if (tableType === 'daysOccMulti') {
                    const columnValue = {val: 10, isDeleted: false};

                    tables[tableType].columns.push(columnValue);
                    tables[tableType].rows.forEach(row => row.values.push({multiVal: 0, isDeleted: false}));
                }
            },
            removeCol: function (tableType, tableSubType, idx) {
                const tables = this.tableData.tables;

                if (tableType === 'channelsMultiplier' || tableType === 'daysOccMulti' || tableType === 'priceMultiplier') {
                    const table = (tableType === 'channelsMultiplier' || tableType === 'priceMultiplier')
                                ? tables[tableType][tableSubType] 
                                : tables[tableType];
                    
                    if (this.isEdit) {
                        table.rows.forEach(row => {
                            row.values[idx].isDeleted = true;
                        });

                        table.columns[idx].isDeleted = true;
                    } else {
                        table.rows.forEach(row => {
                            row.values.splice(idx, 1);
                        });

                        table.columns.splice(idx, 1);
                    }
                }
            },
            removeRow: function (tableType, idx, tableSubType) {
                const tables = this.tableData.tables;
                
                const table = tableType === 'channelsMultiplier'
                            ? tables[tableType][tableSubType]
                            : tables[tableType];

                if (this.isEdit) {
                    table.rows[idx].isDeleted = true;
                } else {
                    table.rows.splice(idx, 1);
                }
            }
        },
        components: {
            Multiselect,
            vSelect: extendedVSelect,
            TernaryCheckbox
        }
    }
</script>

<style lang="scss" scoped>
    .new-product {
        height: -webkit-fill-available;
        overflow-x: auto;
        overflow-y: auto;
        background: white;
        margin: 0px 15px;
        margin-bottom: 15px;
    }

    .w-80 {
        width: 80%;
    }

    .btn-add {
        background-color: #ffffff;
        border: none;
        padding: 5px;
        padding-left: 10px;
    }

    .table-min-height {
        min-height: 200px;
        margin-bottom: 60px;
        margin-top: 35px;
    }
    
    .tr-size {
        height: 45px;
    }
    
    .row-min-height {
        min-height: 100px;
    }

    .var-min-height {
        min-height: 86px;
    }

    .thead-size {
        height: 40px;
    }

    .table-sales {
        thead {
            tr {
                th {
                    border-width: 1px 0;
                    border-style: solid;
                    border-color: #DBDFE2;
                    background: #ffffff;
                    font-size: 0.8rem;
                    color: #8ca0b3;
                }
            }
        }
    }

    .select-width {
        width: 205px;
    }

    .input-col {
        border: none;
        width: 40px;
        color: #8ca0b3;

        &:focus {
            color: black;
        }
    }

    .input-row {
        max-width: fit-content;
    }

    .blocked-input {
        position: relative;
        height: 30px;
        background-color: #f0f0f0;
        padding: 0.1rem 0.6rem;
        border: 1px solid #DBDFE2;
        border-radius: 4px;
        margin: 0.4rem;
        white-space: nowrap;
    }

    .icons-tooltip {
        position: absolute;
        top: -6px;
        right: -15px;
        background-color: white;
    }

    .multi-input {
        max-width: 40px;
        
        &.two {
            height: 30px;
        }
    }

    .closed-row {
        background-color: #f0f0f0;
    }

    .hidden {
        visibility: hidden;
    }

    .days-week-input {
        width: 160px;
    }
</style>


// WEBPACK FOOTER //
// NewRule.vue?b64c72e8